import { computed, ref, triggerRef } from 'vue';

interface UseRecordSelectionOptions {
  /**
   * Upper bound on how many revisions can be selected at once. Mirrors the `@ArrayMaxSize` of the
   * link DTO: without it, "select all" over "Todos os registros" builds a batch the backend
   * rejects whole, and the user only sees the generic failure toast.
   */
  maxSelections?: number;
  selectionLimitMessage?: string;
}

export function useRecordSelection({
  maxSelections,
  selectionLimitMessage = 'O número máximo de registros selecionados foi atingido.',
}: UseRecordSelectionOptions = {}) {
  const selectedRevisionIds = ref<Set<number>>(new Set());
  const selectionLimitError = ref('');

  const isRevisionSelected = (revisionId: number) => selectedRevisionIds.value.has(revisionId);

  const canAddMore = computed(
    () => maxSelections === undefined || selectedRevisionIds.value.size < maxSelections
  );

  /** An already-ticked row stays tickable, so it can be unticked even at the cap. */
  const canSelectRevision = (revisionId: number) =>
    isRevisionSelected(revisionId) || canAddMore.value;

  const clearSelectionLimitError = () => {
    selectionLimitError.value = '';
  };

  const toggleRevisionSelection = (revisionId: number) => {
    if (selectedRevisionIds.value.has(revisionId)) {
      selectedRevisionIds.value.delete(revisionId);
      selectionLimitError.value = '';
      triggerRef(selectedRevisionIds);
      return;
    }

    if (!canAddMore.value) {
      selectionLimitError.value = selectionLimitMessage;
      return;
    }

    selectedRevisionIds.value.add(revisionId);
    selectionLimitError.value = '';
    triggerRef(selectedRevisionIds);
  };

  const selectAllRevisions = (revisionIds: number[]) => {
    const unselected = revisionIds.filter((revisionId) => !isRevisionSelected(revisionId));
    const remaining =
      maxSelections === undefined
        ? unselected.length
        : Math.max(0, maxSelections - selectedRevisionIds.value.size);

    unselected
      .slice(0, remaining)
      .forEach((revisionId) => selectedRevisionIds.value.add(revisionId));

    selectionLimitError.value = unselected.length > remaining ? selectionLimitMessage : '';
    triggerRef(selectedRevisionIds);
  };

  const deselectAllRevisions = () => {
    selectedRevisionIds.value.clear();
    selectionLimitError.value = '';
    triggerRef(selectedRevisionIds);
  };

  const toggleAllRevisions = (revisionIds: number[]) => {
    const allSelected =
      revisionIds.length > 0 &&
      revisionIds.every((revisionId) => selectedRevisionIds.value.has(revisionId));

    if (allSelected) {
      revisionIds.forEach((revisionId) => selectedRevisionIds.value.delete(revisionId));
      selectionLimitError.value = '';
      triggerRef(selectedRevisionIds);
      return;
    }

    selectAllRevisions(revisionIds);
  };

  const selectedCount = computed(() => selectedRevisionIds.value.size);

  const selectedRevisionIdList = computed(() => Array.from(selectedRevisionIds.value));

  const hasSelectedRevisions = computed(() => selectedRevisionIds.value.size > 0);

  return {
    selectedCount,
    selectedRevisionIdList,
    hasSelectedRevisions,
    selectionLimitError,
    canAddMore,
    canSelectRevision,
    clearSelectionLimitError,
    isRevisionSelected,
    toggleRevisionSelection,
    selectAllRevisions,
    deselectAllRevisions,
    toggleAllRevisions,
  };
}
